/**
 * 用途：数据概览仪表板（首页），展示各渠道广告整体消耗、ROI、账户余额等核心指标
 * 路由：/ （根路径，登录后默认落地页）
 */
import { useState, useEffect, useCallback, useMemo, type CSSProperties } from 'react';
import { MultiSelect } from '../components/MultiSelect';
import Pagination from '../components/Pagination';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import {
  AccountSummaryData,
  dashboardApi,
  FilterOptions,
  SummaryData,
  TrendPoint,
  DashboardCampaign,
  DashboardParams,
} from '../api/dashboard';
import { useAppStore } from '../store/useAppStore';
import { useIsMobile } from '../hooks/useIsMobile';
import { sf } from '../lib/fieldPolicy';
import { SecurityBlock } from '../components/SecurityBlock';
import { SortableHeader, useTableSort, type SortValue } from '../components/SortableTable';
import { showAlert } from '../store/useAlertStore';

type DateRange = 'today' | 'yesterday' | '7d' | '30d' | 'custom';

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  today: '今日',
  yesterday: '昨日',
  '7d': '近 7 天',
  '30d': '近 30 天',
  custom: '自定义',
};

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  tiktok: 'TikTok',
  google: 'Google',
};

const PLATFORM_CLASS: Record<string, string> = {
  facebook: 'fb',
  tiktok: 'tt',
  google: 'gg',
  meta: 'fb',
};

const PLATFORM_COLORS: Record<string, string> = {
  facebook: 'var(--fb-color)',
  meta: 'var(--fb-color)',
  tiktok: 'var(--tt-color)',
  google: 'var(--gg-color)',
};

const TABLE_NUMBER_CELL_STYLE: CSSProperties = {
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

// SVG line chart constants
const SVG_W = 600;
const SVG_H = 130;
const PAD = { t: 12, r: 12, b: 24, l: 44 };
// ── Number formatters (all rounded to 2 decimal places) ──────────────────────

function fmt(n: number, prefix = '$'): string {
  if (n >= 1_000_000) return `${prefix}${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${prefix}${(n / 1_000).toFixed(2)}K`;
  return `${prefix}${n.toFixed(2)}`;
}

function fmtAccountBalance(n: number, currency: string): string {
  const normalized = currency.trim().toUpperCase();
  const prefixes: Record<string, string> = {
    USD: '$',
    CNY: '¥',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    KRW: '₩',
  };
  const prefix = prefixes[normalized] ?? (normalized && normalized !== 'UNKNOWN' ? `${normalized} ` : '');
  return fmt(n, prefix);
}

function fmtAxisMoney(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toFixed(0);
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return n.toFixed(0);
}

function fmtChange(change: number, invertBad = false): JSX.Element {
  const up = invertBad ? change < 0 : change > 0;
  const cls = change === 0 ? '' : up ? 'up' : 'down';
  const arrow = change > 0 ? '↑' : change < 0 ? '↓' : '';
  return (
    <div className={`metric-change ${cls}`}>
      {arrow} {Math.abs(change).toFixed(2)}% vs 上期
    </div>
  );
}

// Build chart data: group TrendPoint[] by date → { date, [platform]: spend }
function buildChartData(points: TrendPoint[]) {
  const dateMap: Record<string, Record<string, number>> = {};
  const platformsSet = new Set<string>();
  for (const pt of points) {
    if (!dateMap[pt.date]) dateMap[pt.date] = {};
    dateMap[pt.date][pt.platform] = (dateMap[pt.date][pt.platform] || 0) + pt.spend;
    platformsSet.add(pt.platform);
  }
  const dates = Object.keys(dateMap).sort();
  const platforms = Array.from(platformsSet).sort();
  return { dates, platforms, dateMap };
}

// ── Skeleton components ───────────────────────────────────────────────────────

function SkeletonMetricCards() {
  return (
    <div className="metrics-grid">
      {[
        { cls: 'mc-blue' },
        { cls: 'mc-green' },
        { cls: 'mc-cyan' },
        { cls: 'mc-violet' },
        { cls: 'mc-pink' },
        { cls: 'mc-amber' },
      ].map((c, i) => (
        <div key={i} className={`skeleton-card ${c.cls}`}>
          <div className="skeleton" style={{ height: 11, width: '60%', marginBottom: 12 }} />
          <div className="skeleton" style={{ height: 28, width: '75%', marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 10, width: '45%', marginBottom: 14 }} />
          <div className="skeleton" style={{ height: 32, width: '100%', borderRadius: 2 }} />
        </div>
      ))}
    </div>
  );
}

function SkeletonChart() {
  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
        {[80, 60, 70].map((w, i) => (
          <div key={i} className="skeleton" style={{ height: 10, width: w }} />
        ))}
      </div>
      <div className="skeleton" style={{ height: SVG_H, width: '100%', borderRadius: 6 }} />
    </div>
  );
}

function SkeletonTable() {
  return (
    <div>
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 16,
            padding: '12px 14px',
            borderBottom: '1px solid var(--border)',
            alignItems: 'center',
          }}
        >
          <div className="skeleton" style={{ height: 20, width: 60, borderRadius: 4, flexShrink: 0 }} />
          <div className="skeleton" style={{ height: 12, flex: 3 }} />
          {[1, 1, 1, 1, 1, 1, 1].map((_, j) => (
            <div key={j} className="skeleton" style={{ height: 12, flex: 1 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Compact locked placeholder（metric card 内部锁定态） ─────────────────────
function LockedValue({ hint, level }: { hint?: string; level: 'L2' | 'L3' | 'L4' }) {
  const theme: Record<string, { bg: string; text: string; border: string }> = {
    L2: { bg: '#E6F1FB', text: '#0C447C', border: '#85B7EB' },
    L3: { bg: '#FFFBEB', text: '#BA7517', border: '#FAC775' },
    L4: { bg: '#FEF2F2', text: '#A32D2D', border: '#FECACA' },
  }
  const t = theme[level]
  return (
    <div
      title={hint}
      style={{ display: 'flex', alignItems: 'center', gap: 5, background: t.bg, border: `1px solid ${t.border}`, borderRadius: 5, padding: '4px 10px', color: t.text, fontSize: 12, margin: '4px 0' }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.text} strokeWidth="2.5">
        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      需要更高权限查看
    </div>
  )
}

// ── Masked change placeholder（无权限时百分比显示 *% ） ─────────────────────
function MaskedChange() {
  return (
    <div className="metric-change" style={{ color: '#CCC', letterSpacing: '1px', fontStyle: 'normal' }}>
      *% vs 上期
    </div>
  )
}

// ── Locked sparkline placeholder（无权限时替换迷你图表） ─────────────────────
function LockedSparkline({ level }: { level: 'L2' | 'L3' | 'L4' }) {
  const roleLabel: Record<string, string> = { L2: 'Optimizer', L3: 'Lead', L4: 'Admin' }
  return (
    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#C0C0C0' }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#C0C0C0" strokeWidth="2.5" style={{ flexShrink: 0 }}>
        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      需要 {roleLabel[level]} 权限
    </div>
  )
}

// ── Empty states ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px',
        gap: 16,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 16,
          background: 'var(--surface2)',
          border: '1px solid var(--border2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M9 21V9" />
          <path d="M7 6h.01M11 6h.01" />
        </svg>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>
          暂无数据
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
          当前筛选条件下没有广告成效数据
          <br />
          请尝试调整时间范围或平台筛选
        </div>
      </div>
    </div>
  );
}

function ChartEmpty() {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        minHeight: 130,
      }}
    >
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--border2)" strokeWidth="1.5">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
      <span style={{ fontSize: 12, color: 'var(--text3)' }}>暂无趋势数据</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const PLATFORM_FILTERS = ['facebook', 'google', 'tiktok'] as const;
type PlatformFilter = typeof PLATFORM_FILTERS[number];

export function Dashboard() {
  const navigate = useNavigate();
  const { brandId, siteId, adContext } = useAppStore();
  const isMobile = useIsMobile();
  const userLevel = adContext?.data_level ?? 1;
  const canViewAccountBalance = userLevel >= 4;
  const [showAccountBalance, setShowAccountBalance] = useState(false);

  // ── Filters ──────────────────────────────────────────────
  const [dateRange, setDateRange] = useState<DateRange>('7d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformFilter>('facebook');
  const [accountID, setAccountID] = useState('');

  const googleOnly = selectedPlatform === 'google';

  const selectPlatform = (p: PlatformFilter) => {
    setSelectedPlatform(p);
    setAccountID('');
  };

  useEffect(() => {
    setShowAccountBalance(false);
  }, [brandId, siteId, accountID, selectedPlatform, dateRange, customStart, customEnd]);

  // ── Data ─────────────────────────────────────────────────
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ platforms: [], accounts: [] });
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [accountSummary, setAccountSummary] = useState<AccountSummaryData | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [campaigns, setCampaigns] = useState<DashboardCampaign[]>([]);

  // ── UI state ──────────────────────────────────────────────
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingAccountSummary, setLoadingAccountSummary] = useState(false);
  const [loadingTrend, setLoadingTrend] = useState(false);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [campPage, setCampPage] = useState(1);
  const [campPageSize, setCampPageSize] = useState(10);
  const [selectedCampaign, setSelectedCampaign] = useState<DashboardCampaign | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel' | 'pdf'>('csv');
  const [exportDateRange, setExportDateRange] = useState('current');

  // ── Load filter options when brand/site changes ──────────
  useEffect(() => {
    setAccountID('');
    dashboardApi.getFilterOptions({ brand_id: brandId, site_id: siteId, scope: 'dashboard_read' })
      .then((res: any) => {
        const body = res?.data ?? res ?? {};
        const data: FilterOptions = {
          platforms: Array.isArray(body.platforms) ? body.platforms : [],
          accounts: Array.isArray(body.accounts) ? body.accounts : [],
        };
        setFilterOptions(data);
      })
      .catch(() => {});
  }, [brandId, siteId]);

  // ── Build query params ────────────────────────────────────
  const buildParams = useCallback((): DashboardParams => {
    const p: DashboardParams = { brand_id: brandId, site_id: siteId, date_range: dateRange };
    if (dateRange === 'custom') {
      p.start_date = customStart;
      p.end_date = customEnd;
    }
    p.platform = selectedPlatform;
    if (accountID) p.account_id = accountID;
    return p;
  }, [brandId, siteId, dateRange, customStart, customEnd, selectedPlatform, accountID]);

  // ── Load dashboard data ───────────────────────────────────
  useEffect(() => {
    const params = buildParams();

    setLoadingSummary(true);
    dashboardApi.getSummary(params)
      .then((res: any) => {
        const sum = res?.data ?? null;
        setSummary(sum && typeof sum === 'object' ? sum : null);
      })
      .catch((err: any) => console.warn('Summary API error:', err?.message))
      .finally(() => setLoadingSummary(false));

    setLoadingAccountSummary(true);
    dashboardApi.getAccountSummary(params)
      .then((res: any) => {
        const data = res?.data ?? null;
        setAccountSummary(data && typeof data === 'object' ? data : null);
      })
      .catch((err: any) => console.warn('Account summary API error:', err?.message))
      .finally(() => setLoadingAccountSummary(false));

    setLoadingTrend(true);
    const trendParams = (dateRange === 'today' || dateRange === 'yesterday')
      ? { ...params, date_range: 'last_7_days' }
      : params;
    dashboardApi.getTrend(trendParams)
      .then((res: any) => {
        const tr = res?.data;
        setTrend(Array.isArray(tr) ? tr : []);
      })
      .catch((err: any) => console.warn('Trend API error:', err?.message))
      .finally(() => setLoadingTrend(false));

    setLoadingCampaigns(true);
    dashboardApi.getCampaigns({ ...params, limit: 200 })
      .then((res: any) => {
        const camps = res?.data;
        setCampaigns(Array.isArray(camps) ? camps : []);
        setCampPage(1);
      })
      .catch((err: any) => console.warn('Campaigns API error:', err?.message))
      .finally(() => setLoadingCampaigns(false));
  }, [buildParams]);

  // ── Derived chart data ────────────────────────────────────
  const { dates, platforms: chartPlatforms, dateMap } = buildChartData(trend);

  const maxLineVal = chartPlatforms.reduce(
    (max, p) => dates.reduce((m, d) => Math.max(m, dateMap[d]?.[p] || 0), max),
    0
  );
  const yAxisTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
    ratio,
    value: maxLineVal * (1 - ratio),
  }));

  const channelTotals: Record<string, number> = {};
  for (const pt of trend) {
    channelTotals[pt.platform] = (channelTotals[pt.platform] || 0) + pt.spend;
  }
  const totalSpendAll = Object.values(channelTotals).reduce((s, v) => s + v, 0) || 1;

  // ── Pagination ────────────────────────────────────────────
  const { sort: campaignSort, toggleSort: toggleCampaignSort, sortedRows: sortedCampaigns } = useTableSort<DashboardCampaign, string>(
    campaigns,
    (campaign, key) => campaign[key as keyof DashboardCampaign] as SortValue,
  );
  const handleCampaignSort = useCallback((key: string) => {
    toggleCampaignSort(key);
    setCampPage(1);
  }, [toggleCampaignSort]);
  const campaignSortDisabled = useMemo(() => new Set(['spend', 'revenue', 'cpc', 'cpm']), []);
  const totalPages = Math.max(1, Math.ceil(sortedCampaigns.length / campPageSize));
  const pagedCampaigns = sortedCampaigns.slice((campPage - 1) * campPageSize, campPage * campPageSize);

  // ── Global empty state ────────────────────────────────────
  const anyLoading = loadingSummary || loadingAccountSummary || loadingTrend || loadingCampaigns;
  const noData = !anyLoading && !summary && trend.length === 0 && campaigns.length === 0;

  const handleExport = () => {
    showAlert(`导出 ${exportFormat.toUpperCase()} · ${exportDateRange === 'current' ? '当前筛选' : '自定义'} · ${campaigns.length} 条数据`, { type: 'success' });
    setShowExportModal(false);
  };

  const exportBtn = (
    <button className="btn btn-outline btn-sm" style={{ flexShrink: 0 }} onClick={() => setShowExportModal(true)}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      导出报表
    </button>
  );

  const platformChips = (
    <div className="platform-chips">
      {PLATFORM_FILTERS.map((p) => (
        <span
          key={p}
          className={`platform-chip ${PLATFORM_CLASS[p]} ${selectedPlatform === p ? 'active' : ''}`}
          onClick={() => selectPlatform(p)}
          style={selectedPlatform === p ? undefined : {
            background: 'var(--surface3)',
            borderColor: 'var(--border2)',
            color: 'var(--text2)',
            opacity: 1,
          }}
        >
          {PLATFORM_LABELS[p]}
        </span>
      ))}
    </div>
  );

  return (
    <div className="page-section visible">
      {/* ── Toolbar ─────────────────────────────────────────── */}
      {isMobile ? (
        <div className="toolbar-mobile">
          {/* 行1：时间 tabs（可横向滚动） */}
          <div className="toolbar-mobile-row tabs-row">
            <div className="page-tabs">
              {(Object.keys(DATE_RANGE_LABELS) as DateRange[]).map((dr) => (
                <div
                  key={dr}
                  className={`page-tab ${dateRange === dr ? 'active' : ''}`}
                  onClick={() => setDateRange(dr)}
                >
                  {DATE_RANGE_LABELS[dr]}
                </div>
              ))}
            </div>
          </div>
          {/* 自定义日期 */}
          {dateRange === 'custom' && (
            <div className="toolbar-mobile-row" style={{ gap: 6 }}>
              <input type="date" className="form-input" style={{ flex: 1, fontSize: 12 }} value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
              <input type="date" className="form-input" style={{ flex: 1, fontSize: 12 }} value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
            </div>
          )}
          {/* 行2：平台 chips + 导出 */}
          <div className="toolbar-mobile-row">
            {platformChips}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              {anyLoading && <div className="spinner" />}
              {exportBtn}
            </div>
          </div>
        </div>
      ) : (
        <div className="toolbar">
          <div className="page-tabs">
            {(Object.keys(DATE_RANGE_LABELS) as DateRange[]).map((dr) => (
              <div
                key={dr}
                className={`page-tab ${dateRange === dr ? 'active' : ''}`}
                onClick={() => setDateRange(dr)}
              >
                {DATE_RANGE_LABELS[dr]}
              </div>
            ))}
          </div>

          {dateRange === 'custom' && (
            <>
              <div className="divider-v" />
              <input type="date" className="form-input" style={{ width: 130, fontSize: 12 }} value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
              <input type="date" className="form-input" style={{ width: 130, fontSize: 12 }} value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
            </>
          )}

          <div className="divider-v" />
          {platformChips}

          <MultiSelect
            single
            options={[
              { value: '', label: '全部账户' },
              ...filterOptions.accounts
                .filter((a) => a.platform === selectedPlatform)
                .filter((a, i, arr) => arr.findIndex(x => x.account_id === a.account_id) === i)
                .map((a) => ({ value: a.account_id, label: a.account_name || a.account_id })),
            ]}
            value={accountID ? [accountID] : ['']}
            onChange={vals => setAccountID(vals[0] === '' ? '' : (vals[0] ?? ''))}
            style={{ width: 180, fontSize: 12 }}
          />

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            {anyLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div className="spinner" />
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>加载中</span>
              </div>
            )}
            {exportBtn}
          </div>
        </div>
      )}

      {/* ── Global empty state ───────────────────────────────── */}
      {noData ? (
        <EmptyState />
      ) : (
        <>
          <div className="card mb-24">
            <div className="card-header">
              <div>
                <div className="card-title">广告账户信息</div>
                <div className="card-subtitle">{accountID ? '当前单账户视角' : '当前筛选账户汇总'}</div>
              </div>
            </div>
            {loadingAccountSummary ? (
              <SkeletonMetricCards />
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                  gap: 12,
                }}
              >
                {[
                  {
                    key: 'balance',
                    label: '账户余额',
                    value: canViewAccountBalance && showAccountBalance
                      ? (accountSummary?.balance_mixed_currency
                        ? '多币种'
                        : accountSummary?.balance_available
                          ? fmtAccountBalance(accountSummary?.balance ?? 0, accountSummary?.balance_currency ?? '')
                          : '—')
                      : '$*** *** **',
                    hint: !canViewAccountBalance
                      ? '需要 Admin 权限查看账户余额'
                      : showAccountBalance
                      ? (accountSummary?.balance_mixed_currency
                        ? '账户币种不同，不能直接汇总'
                        : accountSummary?.balance_available
                        ? ((accountSummary?.balance_missing_count ?? 0) > 0
                          ? `${accountSummary?.balance_missing_count ?? 0} 个账户余额缺失`
                          : '当前筛选账户余额已同步')
                        : googleOnly
                          ? 'Google 当前未返回可汇总余额，请检查月结预算或同步日志'
                          : '当前账户未同步余额')
                      : '数据已脱敏，完整数据需有查看权限',
                  },
                  {
                    key: 'available_days',
                    label: '预测可用天数',
                    value: canViewAccountBalance && showAccountBalance
                      ? (accountSummary?.balance_mixed_currency
                        || !accountSummary?.balance_available
                        || (accountSummary?.last_7_days_avg_spend ?? 0) <= 0
                        ? '—'
                        : `${(accountSummary?.available_days ?? 0).toFixed(1)} 天`)
                      : '* 天',
                    hint: canViewAccountBalance && showAccountBalance
                      ? (accountSummary?.balance_mixed_currency
                        ? '账户币种不同，无法计算可用天数'
                        : !accountSummary?.balance_available
                          ? '余额不可用，无法预测可用天数'
                          : (accountSummary?.last_7_days_avg_spend ?? 0) <= 0
                            ? '近 7 天无花费，无法预测可用天数'
                            : `按有余额账户近 7 天日均花费 ${fmtAccountBalance(accountSummary?.last_7_days_avg_spend ?? 0, accountSummary?.balance_currency ?? '')} 计算`)
                      : '数据已脱敏，完整数据需有查看权限',
                  },
                  {
                    key: 'active_count',
                    label: '投放中广告计划',
                    value: fmtCount(accountSummary?.active_count ?? 0),
                    hint: `覆盖 ${fmtCount(accountSummary?.account_count ?? 0)} 个账户`,
                  },
                  {
                    key: 'inactive_count',
                    label: '未投放广告计划',
                    value: fmtCount(accountSummary?.inactive_count ?? 0),
                    hint: accountID ? '当前单账户范围' : '当前筛选账户范围',
                  },
                ].map((item) => (
                  <div key={item.key} className="metric-card" style={{ minHeight: 132 }}>
                    <div className="metric-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{item.label}</span>
                      {item.key === 'balance' && (
                        <button
                          type="button"
                          aria-label={showAccountBalance ? '隐藏账户余额' : '显示账户余额'}
                          title={canViewAccountBalance ? (showAccountBalance ? '隐藏账户余额' : '显示账户余额') : '需要 Admin 权限查看账户余额'}
                          disabled={!canViewAccountBalance}
                          onClick={() => setShowAccountBalance((v) => !v)}
                          onBlur={() => setShowAccountBalance(false)}
                          style={{
                            width: 20,
                            height: 20,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            color: canViewAccountBalance ? 'var(--text3)' : 'var(--text4)',
                            cursor: canViewAccountBalance ? 'pointer' : 'not-allowed',
                          }}
                        >
                          {showAccountBalance ? <Eye size={15} strokeWidth={2.2} /> : <EyeOff size={15} strokeWidth={2.2} />}
                        </button>
                      )}
                    </div>
                    <div className="metric-value">{item.value}</div>
                    <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>{item.hint}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── KPI Cards ──────────────────────────────────────── */}
          {loadingSummary ? (
            <SkeletonMetricCards />
          ) : (
            <div className="metrics-grid">
              {/* Spend — L2 内部 */}
              <div className="metric-card mc-blue">
                <div className="metric-label">总花费 (Spend)</div>
                {(() => {
                  if (summary?.spend == null) return <><div className="metric-value">—</div><div className="mini-sparkline" style={{ marginTop: 10 }}>{[40,55,45,70,60,80,75,90,85,100,92,88].map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}</div></>
                  const r = sf(fmt(summary.spend.value ?? 0), 'spend', userLevel)
                  return (
                    <>
                      {r.display === 'locked'
                        ? <LockedValue level="L2" hint={r.hint} />
                        : <><div className="metric-value">{r.value}</div>{r.display === 'mask' ? <MaskedChange /> : fmtChange(summary.spend.change ?? 0)}</>
                      }
                      {r.display !== 'show'
                        ? <LockedSparkline level="L2" />
                        : <div className="mini-sparkline" style={{ marginTop: 10 }}>{[40,55,45,70,60,80,75,90,85,100,92,88].map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}</div>
                      }
                    </>
                  )
                })()}
              </div>

              {/* ROAS — L1 公开 */}
              <div className="metric-card mc-green">
                <div className="metric-label">整体 ROAS</div>
                <div className="metric-value" style={{ color: 'var(--green)' }}>
                  {summary?.roas != null ? `${(summary.roas.value ?? 0).toFixed(2)}x` : '—'}
                </div>
                {summary?.roas != null && fmtChange(summary.roas.change ?? 0)}
                <div className="mini-sparkline" style={{ marginTop: 10 }}>
                  {[60,55,65,70,75,72,80,85,82,90,95,100].map((h, i) => (
                    <span key={i} style={{ height: `${h}%`, background: 'var(--green)' }} />
                  ))}
                </div>
              </div>

              {/* CPC — L2 内部 */}
              <div className="metric-card mc-cyan">
                <div className="metric-label">CPC (单次点击费用)</div>
                {(() => {
                  if (summary?.cpc == null) return <><div className="metric-value">—</div><div className="mini-sparkline" style={{ marginTop: 10 }}>{[100,95,92,88,85,80,78,72,68,65,60,55].map((h, i) => <span key={i} style={{ height: `${h}%`, background: 'var(--cyan)' }} />)}</div></>
                  const r = sf(fmt(summary.cpc.value ?? 0), 'cpc', userLevel)
                  return (
                    <>
                      {r.display === 'locked'
                        ? <LockedValue level="L2" hint={r.hint} />
                        : <><div className="metric-value">{r.value}</div>{r.display === 'mask' ? <MaskedChange /> : fmtChange(summary.cpc.change ?? 0, true)}</>
                      }
                      {r.display !== 'show'
                        ? <LockedSparkline level="L2" />
                        : <div className="mini-sparkline" style={{ marginTop: 10 }}>{[100,95,92,88,85,80,78,72,68,65,60,55].map((h, i) => <span key={i} style={{ height: `${h}%`, background: 'var(--cyan)' }} />)}</div>
                      }
                    </>
                  )
                })()}
              </div>

              {/* CPM — L2 内部 */}
              <div className="metric-card mc-violet">
                <div className="metric-label">CPM (千次展示费用)</div>
                {(() => {
                  if (summary?.cpm == null) return <><div className="metric-value" style={{ color: 'var(--violet)' }}>—</div><div className="mini-sparkline" style={{ marginTop: 10 }}>{[55,60,70,65,75,80,72,85,90,88,95,100].map((h, i) => <span key={i} style={{ height: `${h}%`, background: 'var(--violet)' }} />)}</div></>
                  const r = sf(fmt(summary.cpm.value ?? 0), 'cpm', userLevel)
                  return (
                    <>
                      {r.display === 'locked'
                        ? <LockedValue level="L2" hint={r.hint} />
                        : <><div className="metric-value" style={{ color: 'var(--violet)' }}>{r.value}</div>{r.display === 'mask' ? <MaskedChange /> : fmtChange(summary.cpm.change ?? 0, true)}</>
                      }
                      {r.display !== 'show'
                        ? <LockedSparkline level="L2" />
                        : <div className="mini-sparkline" style={{ marginTop: 10 }}>{[55,60,70,65,75,80,72,85,90,88,95,100].map((h, i) => <span key={i} style={{ height: `${h}%`, background: 'var(--violet)' }} />)}</div>
                      }
                    </>
                  )
                })()}
              </div>

              {/* Purchase Count — L1 公开 */}
              <div className="metric-card mc-pink">
                <div className="metric-label">购买数量</div>
                <div className="metric-value">{summary?.purchase_count != null ? fmtCount(summary.purchase_count.value ?? 0) : '—'}</div>
                {summary?.purchase_count != null && fmtChange(summary.purchase_count.change ?? 0)}
                <div className="mini-sparkline" style={{ marginTop: 10 }}>
                  {[55,60,58,72,68,80,78,85,90,88,92,100].map((h, i) => (
                    <span key={i} style={{ height: `${h}%`, background: 'var(--pink)' }} />
                  ))}
                </div>
              </div>

              {/* CPA — L1 公开 */}
              <div className="metric-card mc-amber">
                <div className="metric-label">CPA (单客成本)</div>
                <div className="metric-value">{summary?.cpa != null ? fmt(summary.cpa.value ?? 0) : '—'}</div>
                {summary?.cpa != null && fmtChange(summary.cpa.change ?? 0, true)}
                <div className="mini-sparkline" style={{ marginTop: 10 }}>
                  {[100,95,92,88,85,80,78,72,68,65,60,55].map((h, i) => (
                    <span key={i} style={{ height: `${h}%`, background: 'var(--amber)' }} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Charts Row ─────────────────────────────────────── */}
          <div className="grid-23 mb-24">
            {/* Channel Spend Trend — L2 内部数据 */}
            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">各渠道花费趋势</div>
                  <div className="card-subtitle">按天统计各平台广告支出</div>
                </div>
              </div>
              <div className="chart-area" style={userLevel < 2 ? { justifyContent: 'center' } : undefined}>
                <SecurityBlock level="L2" userLevel={userLevel} variant="compact">
                {loadingTrend ? (
                  <SkeletonChart />
                ) : dates.length > 0 ? (
                  <>
                    {/* Legend */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                      {chartPlatforms.map((p) => (
                        <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, color: 'var(--text3)' }}>
                          <div style={{ width: 16, height: 1.5, borderRadius: 1, background: PLATFORM_COLORS[p] || '#1210B8' }} />
                          {PLATFORM_LABELS[p] || p}
                        </div>
                      ))}
                    </div>
                    {/* SVG Line Chart */}
                    <svg
                      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
                      style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible', aspectRatio: `${SVG_W} / ${SVG_H}` }}
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <line
                        x1={PAD.l}
                        y1={PAD.t}
                        x2={PAD.l}
                        y2={SVG_H - PAD.b}
                        stroke="rgba(0,0,0,0.06)"
                        strokeWidth="0.8"
                      />
                      <line
                        x1={PAD.l}
                        y1={SVG_H - PAD.b}
                        x2={SVG_W - PAD.r}
                        y2={SVG_H - PAD.b}
                        stroke="rgba(0,0,0,0.06)"
                        strokeWidth="0.8"
                      />
                      {yAxisTicks.map(({ ratio, value }) => {
                        const y = PAD.t + (SVG_H - PAD.t - PAD.b) * ratio;
                        return (
                          <g key={ratio}>
                            <line
                              x1={PAD.l}
                              y1={y}
                              x2={SVG_W - PAD.r}
                              y2={y}
                              stroke="rgba(0,0,0,0.03)"
                              strokeWidth="0.8"
                            />
                            <text
                              x={PAD.l - 6}
                              y={y + 3}
                              textAnchor="end"
                              fontSize="7"
                              fill="var(--text3)"
                            >
                              {fmtAxisMoney(value)}
                            </text>
                          </g>
                        );
                      })}
                      {chartPlatforms.map((p) => {
                        const color = PLATFORM_COLORS[p] || '#1210B8';
                        const cw = SVG_W - PAD.l - PAD.r;
                        const ch = SVG_H - PAD.t - PAD.b;
                        const n = dates.length;
                        const ptArr = dates.map((date, i) => {
                          const x = PAD.l + (n === 1 ? cw / 2 : (i / (n - 1)) * cw);
                          const val = dateMap[date]?.[p] || 0;
                          const y = PAD.t + ch * (1 - val / Math.max(maxLineVal, 1));
                          return { x, y, val, index: i };
                        });
                        const points = ptArr.map(({ x, y }) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
                        const firstX = ptArr[0].x;
                        const lastX = ptArr[ptArr.length - 1].x;
                        const baseY = PAD.t + ch;
                        const areaPath =
                          `M ${firstX},${baseY} ` +
                          ptArr.map(({ x, y }) => `L ${x.toFixed(1)},${y.toFixed(1)}`).join(' ') +
                          ` L ${lastX},${baseY} Z`;
                        return (
                          <g key={p}>
                            <path d={areaPath} fill={color} fillOpacity="0.06" />
                            <polyline points={points} fill="none" stroke={color}
                              strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                            {ptArr.map(({ x, y, val, index }) => {
                              const showLabel = n <= 7 || index === n - 1 || (n <= 14 && index % 2 === 0);
                              return (
                                <g key={index}>
                                  {showLabel && (
                                    <>
                                      <rect
                                        x={(x - 11).toFixed(1)}
                                        y={(Math.max(PAD.t, y - 12)).toFixed(1)}
                                        width="22"
                                        height="8"
                                        rx="4"
                                        fill="rgba(255,255,255,0.88)"
                                        stroke={color}
                                        strokeOpacity="0.1"
                                        strokeWidth="0.8"
                                      />
                                      <text
                                        x={x.toFixed(1)}
                                        y={(Math.max(PAD.t + 5.5, y - 6.5)).toFixed(1)}
                                        textAnchor="middle"
                                        fontSize="6"
                                        fill={color}
                                      >
                                        {fmtAxisMoney(val)}
                                      </text>
                                    </>
                                  )}
                                  <circle cx={x.toFixed(1)} cy={y.toFixed(1)} r="2.2"
                                fill={color} stroke="var(--surface)" strokeWidth="1" />
                                </g>
                              );
                            })}
                          </g>
                        );
                      })}
                      {dates.map((date, i) => {
                        const cw = SVG_W - PAD.l - PAD.r;
                        const n = dates.length;
                        const x = PAD.l + (n === 1 ? cw / 2 : (i / (n - 1)) * cw);
                        if (n > 10 && i % 2 !== 0) return null;
                        return (
                          <text key={date} x={x.toFixed(1)} y={SVG_H - 4}
                            textAnchor="middle" fontSize="7" fill="var(--text3)">
                            {date.slice(5)}
                          </text>
                        );
                      })}
                    </svg>
                  </>
                ) : (
                  <ChartEmpty />
                )}
                </SecurityBlock>
              </div>
            </div>

            {/* Budget Distribution — L2 内部数据 */}
            <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="card-header">
                <div className="card-title">渠道预算分布</div>
              </div>
              <SecurityBlock level="L2" userLevel={userLevel} variant="compact">
              {loadingTrend ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
                  {[1, 2, 3].map((i) => (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div className="skeleton" style={{ height: 12, width: 80 }} />
                        <div className="skeleton" style={{ height: 12, width: 60 }} />
                      </div>
                      <div className="skeleton" style={{ height: 6, width: '100%', borderRadius: 3 }} />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
                  {Object.entries(channelTotals)
                    .sort((a, b) => b[1] - a[1])
                    .map(([p, spend]) => {
                      const pct = ((spend / totalSpendAll) * 100).toFixed(2);
                      const cls = PLATFORM_CLASS[p] || 'fb';
                      return (
                        <div key={p}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span className={`ch-dot ${cls}`} />
                              {PLATFORM_LABELS[p] || p}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 700 }}>
                              {pct}% · {fmt(spend)}
                            </span>
                          </div>
                          <div className="progress">
                            <div className="progress-fill" style={{ width: `${pct}%`, background: `var(--${cls}-color)` }} />
                          </div>
                        </div>
                      );
                    })}
                  {Object.keys(channelTotals).length === 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '24px 0' }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--border2)" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 2a10 10 0 0 1 0 20" />
                        <path d="M2 12h20" />
                      </svg>
                      <span style={{ fontSize: 12, color: 'var(--text3)' }}>暂无数据</span>
                    </div>
                  )}
                </div>
              )}
              </SecurityBlock>
            </div>
          </div>

          {/* ── Top Campaigns Table ─────────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Top 广告计划 (按花费排序)</div>
                <div className="card-subtitle">
                  数据来源: ad_insights · 当前账户 + 时间范围
                </div>
              </div>
            </div>
            {loadingCampaigns ? (
              <SkeletonTable />
            ) : isMobile ? (
              /* 移动端：卡片列表 */
              <div>
                {campaigns.length === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '32px 0' }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--border2)" strokeWidth="1.5">
                      <path d="M9 17H5a2 2 0 0 0-2 2" /><path d="M9 3H5a2 2 0 0 0-2 2v4" /><path d="M3 11v2" /><rect x="11" y="3" width="10" height="18" rx="2" />
                    </svg>
                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>暂无广告计划数据，请调整筛选条件</span>
                  </div>
                ) : (
                  pagedCampaigns.map((c, idx) => {
                    const cls = PLATFORM_CLASS[c.platform] || 'fb';
                    const label = PLATFORM_LABELS[c.platform] || c.platform;
                    const roasColor = (c.roas ?? 0) >= 3 ? 'var(--green)' : (c.roas ?? 0) >= 2 ? 'var(--amber)' : 'var(--red)';
                    return (
                      <div
                        key={`${c.campaign_id}-${idx}`}
                        onClick={() => setSelectedCampaign(c)}
                        style={{ padding: '12px 0', borderBottom: idx < pagedCampaigns.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}
                      >
                        {/* 行1：平台 + 名称 + 状态 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <span className={`ch-tag ${cls}`} style={{ fontSize: 10, flexShrink: 0 }}>{label}</span>
                          <span style={{ fontWeight: 600, fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.campaign_name ?? '—'}
                          </span>
                          <span className={`status ${c.status === 'active' ? 'status-active' : 'status-paused'}`} style={{ flexShrink: 0 }}>
                            {c.status === 'active' ? '投放中' : c.status === 'paused' ? '已暂停' : c.status}
                          </span>
                        </div>
                        {/* 行2：核心指标 */}
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 4 }}>
                          {[
                            { label: '花费', value: fmt(c.spend ?? 0), color: 'var(--text)' },
                            { label: '收入', value: fmt(c.revenue ?? 0), color: 'var(--text)' },
                            { label: 'ROAS', value: `${(c.roas ?? 0).toFixed(2)}x`, color: roasColor },
                            { label: 'CPA', value: fmt(c.cpa ?? 0), color: 'var(--text)' },
                          ].map((m) => (
                            <div key={m.label} style={{ background: 'var(--surface2)', borderRadius: 6, padding: '5px 8px' }}>
                              <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>{m.label}</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: m.color }}>{m.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              /* 桌面端：表格 */
              <div className="table-wrap">
                <table style={{ minWidth: 1120, tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 90 }} />
                    <col style={{ width: 480 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 80 }} />
                    <col style={{ width: 80 }} />
                    <col style={{ width: 80 }} />
                    <col style={{ width: 80 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 100 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <SortableHeader sortKey="platform" sort={campaignSort} onSort={handleCampaignSort}>渠道</SortableHeader>
                      <SortableHeader sortKey="campaign_name" sort={campaignSort} onSort={handleCampaignSort}>广告计划</SortableHeader>
                      <SortableHeader sortKey="spend" sort={campaignSort} onSort={handleCampaignSort} align="right" disabled={userLevel < 2 && campaignSortDisabled.has('spend')}>花费</SortableHeader>
                      <SortableHeader sortKey="revenue" sort={campaignSort} onSort={handleCampaignSort} align="right" disabled={userLevel < 2 && campaignSortDisabled.has('revenue')}>收入</SortableHeader>
                      <SortableHeader sortKey="roas" sort={campaignSort} onSort={handleCampaignSort} align="right">ROAS</SortableHeader>
                      <SortableHeader sortKey="cpc" sort={campaignSort} onSort={handleCampaignSort} align="right" disabled={userLevel < 2 && campaignSortDisabled.has('cpc')}>CPC</SortableHeader>
                      <SortableHeader sortKey="cpm" sort={campaignSort} onSort={handleCampaignSort} align="right" disabled={userLevel < 2 && campaignSortDisabled.has('cpm')}>CPM</SortableHeader>
                      <SortableHeader sortKey="cpa" sort={campaignSort} onSort={handleCampaignSort} align="right">CPA</SortableHeader>
                      <SortableHeader sortKey="purchase_count" sort={campaignSort} onSort={handleCampaignSort} align="right">购买数量</SortableHeader>
                      <SortableHeader sortKey="status" sort={campaignSort} onSort={handleCampaignSort}>状态</SortableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedCampaigns.map((c, idx) => {
                      const cls = PLATFORM_CLASS[c.platform] || 'fb';
                      const label = PLATFORM_LABELS[c.platform] || c.platform;
                      return (
                        <tr key={`${c.campaign_id}-${idx}`} style={{ cursor: 'pointer' }} onClick={() => setSelectedCampaign(c)}>
                          <td><span className={`ch-tag ${cls}`}>{label}</span></td>
                          <td style={{ fontWeight: 600 }}>
                            <span
                              title={c.campaign_name ?? ''}
                              style={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {c.campaign_name ?? '—'}
                            </span>
                          </td>
                          <td style={TABLE_NUMBER_CELL_STYLE}>{sf(fmt(c.spend ?? 0), 'spend', userLevel).value ?? '🔒'}</td>
                          <td style={TABLE_NUMBER_CELL_STYLE}>{sf(fmt(c.revenue ?? 0), 'revenue', userLevel).value ?? '🔒'}</td>
                          <td style={{ ...TABLE_NUMBER_CELL_STYLE, fontWeight: 700, color: (c.roas ?? 0) >= 3 ? 'var(--green)' : (c.roas ?? 0) >= 2 ? 'var(--amber)' : 'var(--red)' }}>
                            {(c.roas ?? 0).toFixed(2)}x
                          </td>
                          <td style={TABLE_NUMBER_CELL_STYLE}>{sf(fmt(c.cpc ?? 0), 'cpc', userLevel).value ?? '🔒'}</td>
                          <td style={TABLE_NUMBER_CELL_STYLE}>{sf(fmt(c.cpm ?? 0), 'cpm', userLevel).value ?? '🔒'}</td>
                          <td style={TABLE_NUMBER_CELL_STYLE}>{fmt(c.cpa ?? 0)}</td>
                          <td style={TABLE_NUMBER_CELL_STYLE}>{fmtCount(c.purchase_count ?? 0)}</td>
                          <td>
                            <span className={`status ${c.status === 'active' ? 'status-active' : 'status-paused'}`}>
                              {c.status === 'active' ? '投放中' : c.status === 'paused' ? '已暂停' : c.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {campaigns.length === 0 && (
                      <tr>
                        <td colSpan={10}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '32px 0' }}>
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--border2)" strokeWidth="1.5">
                              <path d="M9 17H5a2 2 0 0 0-2 2" /><path d="M9 3H5a2 2 0 0 0-2 2v4" /><path d="M3 11v2" /><rect x="11" y="3" width="10" height="18" rx="2" />
                            </svg>
                            <span style={{ fontSize: 12, color: 'var(--text3)' }}>暂无广告计划数据，请调整筛选条件</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loadingCampaigns && campaigns.length > 0 && (
              <Pagination page={campPage} totalPages={totalPages} onChange={setCampPage} total={`共 ${campaigns.length} 条广告计划`} pageSize={campPageSize} pageSizeOptions={[10, 20, 50, 100]} onPageSizeChange={nextPageSize => { setCampPageSize(nextPageSize); setCampPage(1); }} />
            )}
          </div>
        </>
      )}

      {/* ── Export Modal ─────────────────────────────────────── */}
      {showExportModal && (
        <div className="modal-overlay open" onClick={() => setShowExportModal(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>导出报表</h3>
              <button className="modal-close" onClick={() => setShowExportModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">导出格式</label>
                <div className="option-cards">
                  {[
                    { value: 'csv', icon: '📄', title: 'CSV', desc: '逗号分隔' },
                    { value: 'excel', icon: '📊', title: 'Excel', desc: '工作表' },
                    { value: 'pdf', icon: '📑', title: 'PDF', desc: '文档' },
                  ].map((opt) => (
                    <div
                      key={opt.value}
                      className={`option-card ${exportFormat === opt.value ? 'selected' : ''}`}
                      onClick={() => setExportFormat(opt.value as 'csv' | 'excel' | 'pdf')}
                    >
                      <div className="oc-icon">{opt.icon}</div>
                      <div className="oc-title">{opt.title}</div>
                      <div className="oc-desc">{opt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">时间范围</label>
                <MultiSelect
                  single
                  options={[
                    { value: 'current', label: '当前筛选范围' },
                    { value: 'today', label: '今日' },
                    { value: 'yesterday', label: '昨日' },
                    { value: '7d', label: '近 7 天' },
                    { value: '30d', label: '近 30 天' },
                  ]}
                  value={exportDateRange ? [exportDateRange] : []}
                  onChange={vals => setExportDateRange(vals[0] ?? '')}
                />
              </div>
              <div className="form-group">
                <label className="form-label">包含数据</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {['花费数据', '转化数据', 'CPC/CPA/CPM', '素材数据'].map((item, idx) => (
                    <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" defaultChecked />
                      <span style={{ fontSize: 13 }}>{item}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowExportModal(false)}>取消</button>
              <button className="btn btn-primary" onClick={handleExport}>确认导出</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Campaign Detail Side Panel ───────────────────────── */}
      {selectedCampaign && (
        <>
          <div className="panel-backdrop open" onClick={() => setSelectedCampaign(null)} />
          <div className="side-panel open">
            <div className="panel-header">
              <h3>广告计划详情</h3>
              <button className="modal-close" onClick={() => setSelectedCampaign(null)}>&times;</button>
            </div>
            <div className="panel-body">
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span className={`ch-tag ${PLATFORM_CLASS[selectedCampaign.platform] || 'fb'}`}>
                    {PLATFORM_LABELS[selectedCampaign.platform] || selectedCampaign.platform}
                  </span>
                  <span className={`status ${selectedCampaign.status === 'active' ? 'status-active' : 'status-paused'}`}>
                    {selectedCampaign.status === 'active' ? '投放中' : '已暂停'}
                  </span>
                </div>
                <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                  {selectedCampaign.campaign_name}
                </h2>
                <p style={{ fontSize: 12, color: 'var(--text3)' }}>
                  账户 {selectedCampaign.account_name || selectedCampaign.account_id}
                </p>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                  marginBottom: 24,
                }}
              >
                {[
                  { label: '总花费', value: fmt(selectedCampaign.spend ?? 0) },
                  { label: 'ROAS', value: `${(selectedCampaign.roas ?? 0).toFixed(2)}x`, color: 'var(--green)' },
                  { label: 'CPC', value: fmt(selectedCampaign.cpc ?? 0) },
                  { label: 'CPM', value: fmt(selectedCampaign.cpm ?? 0), color: 'var(--violet)' },
                  { label: 'CPA', value: fmt(selectedCampaign.cpa ?? 0) },
                  { label: '购买数量', value: fmtCount(selectedCampaign.purchase_count ?? 0) },
                ].map((item) => (
                  <div key={item.label} className="exp-metric-box">
                    <div className="em-value" style={item.color ? { color: item.color } : {}}>
                      {item.value}
                    </div>
                    <div className="em-label">{item.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 20 }}>
                <div className="eds-label" style={{ marginBottom: 8 }}>收入 (转化金额)</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{fmt(selectedCampaign.revenue ?? 0)}</div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                <button
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  onClick={() => {
                    const platform = selectedCampaign.platform === 'google'
                      ? 'google'
                      : selectedCampaign.platform === 'tiktok'
                        ? 'tiktok'
                        : 'facebook';
                    const params = new URLSearchParams();
                    if (selectedCampaign.campaign_name?.trim()) params.set('search', selectedCampaign.campaign_name.trim());
                    navigate(`/campaigns/${platform}?${params.toString()}`);
                  }}
                >
                  查看广告计划
                </button>
                <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setSelectedCampaign(null)}>
                  关闭
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
